/**
 * Shared user bubble color definitions and utilities.
 * Used by ThemeDialog and anywhere user avatars are displayed.
 */

export const USER_BUBBLE_COLORS = [
  { name: 'Default', value: 'default', textTone: 'auto' as const },
  // Solid colors
  { name: 'Blue', value: '#3b82f6', textTone: 'light' as const },
  { name: 'Purple', value: '#8b5cf6', textTone: 'light' as const },
  { name: 'Pink', value: '#ec4899', textTone: 'light' as const },
  { name: 'Red', value: '#ef4444', textTone: 'light' as const },
  { name: 'Orange', value: '#f97316', textTone: 'light' as const },
  { name: 'Yellow', value: '#eab308', textTone: 'light' as const },
  { name: 'Green', value: '#22c55e', textTone: 'light' as const },
  { name: 'Emerald', value: '#10b981', textTone: 'light' as const },
  { name: 'Teal', value: '#14b8a6', textTone: 'light' as const },
  { name: 'Cyan', value: '#06b6d4', textTone: 'light' as const },
  { name: 'Indigo', value: '#6366f1', textTone: 'light' as const },
  { name: 'Gray', value: '#6b7280', textTone: 'dark' as const },
  // Gradients
  { name: 'Hazy', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTone: 'light' as const },
  { name: 'Sunset', value: 'linear-gradient(135deg, #ff7e5f 0%, #feb47b 100%)', textTone: 'light' as const },
  { name: 'Forest', value: 'linear-gradient(135deg, #134e5e 0%, #71b280 100%)', textTone: 'light' as const },
  { name: 'Fire', value: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', textTone: 'light' as const },
  { name: 'Aurora', value: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)', textTone: 'dark' as const },
  { name: 'Galaxy', value: 'linear-gradient(135deg, #2c3e50 0%, #4ca1af 100%)', textTone: 'light' as const },
  { name: 'Peachy', value: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)', textTone: 'dark' as const },
  { name: 'Lavender', value: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)', textTone: 'dark' as const },
  { name: 'Ocean', value: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)', textTone: 'light' as const },
  { name: 'Rose', value: 'linear-gradient(135deg, #e91e63 0%, #f8bbd9 100%)', textTone: 'light' as const },
  { name: 'Smoky', value: 'linear-gradient(135deg, #1a1a1a 0%, #9b9b9b 100%)', textTone: 'light' as const },
];


/** Cookie get utility for enterprise environments where localStorage may be unreliable. */
export function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

/** Cookie set utility. */
export function setCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  const date = new Date();
  date.setTime(date.getTime() + (365 * 24 * 60 * 60 * 1000));
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
}

/** Returns the saved user bubble color from localStorage or cookie. */
export function getSavedBubbleColor(): string {
  const defaultColor = USER_BUBBLE_COLORS[0].value;
  // Guard for non-browser environments and handle restricted storage.
  if (typeof window !== 'undefined') {
    try {
      const stored = window.localStorage?.getItem('user-bubble-color');
      if (stored) {
        return stored;
      }
    } catch {
      // Ignore storage errors and fall back to cookie/default.
    }
  }
  return getCookie('user-bubble-color') || defaultColor;
}

/**
 * Returns the CSS background and text color for an avatar given the bubble color value and theme mode.
 */
export function getBubbleStyle(
  userBubbleColor: string,
  mode: 'light' | 'dark' | string
): { background: string; color: string } {
  if (userBubbleColor === 'default') {
    return {
      background: mode === 'dark' ? '#313136' : '#e5e7eb',
      color: mode === 'dark' ? '#ffffff' : '#1f2937',
    };
  }
  const meta = USER_BUBBLE_COLORS.find(c => c.value === userBubbleColor);
  const tone = meta?.textTone || 'light';
  const forceDark = tone === 'dark' || (tone === 'auto' && mode === 'light');
  return {
    background: userBubbleColor,
    color: forceDark ? '#1f2937' : '#ffffff',
  };
}
